import { splitArrayToChunks } from "utility/helpers";
import React from "react";
import { SORT_DIRECTION_ASC } from "constants/table";
import {
  ACCOUNT_AGENCY,
  SUPER_ADMIN_ACCOUNTS,
  ACCOUNT_EDITOR,
  canEditRoles,
} from "utility/constants";
import { ALLOCATION_STATUS, ALLOCATION_STATUS_OPTIONS } from "constants/status";
import { AGENCY_TESDA_ID } from "constants/agency";
import styled from "styled-components";

export const getFormattedAgencies = (data) => {
  const newData = data.length >= 3 ? splitArrayToChunks(data, 3) : data;
  let resultData = [];

  resultData =
    newData.length >= 3
      ? newData?.length !== 0 &&
        newData.map((datum) => {
          return (
            datum &&
            datum?.length !== 0 &&
            datum?.map((d) => {
              return {
                photo: d?.photo,
                label: d?.name,
                link: `/projects/${d?.id}`,
                pendingCount: d?.pendingCount,
                requestedCount: d?.requestedCount,
                acronym: d?.acronym,
                id: d?.id,
              };
            })
          );
        })
      : newData?.map((d) => {
          return {
            photo: d?.photo,
            label: d?.name,
            link: `/projects/${d?.id}`,
            pendingCount: d?.pendingCount,
            requestedCount: d?.requestedCount,
            acronym: d?.acronym,
            id: d?.id,
          };
        });

  return resultData;
};

const SelectorLabel = styled.h5`
  font-weight: bold;
  margin: 0;
`;

const SelectorDisplay = styled.div`
  display: flex;
  align-items: center;
`;

const SelectorImage = styled.img`
  height: 70px;
  width: 70px;
  margin-right: 0.5rem;
`;

export const getAgencySelectorOptions = (data) => {
  const options =
    data?.map((select) => ({
      ID: select?.id,
      Name: select?.name,
      acronym: select?.acronym,
      displayLabel: (
        <SelectorDisplay>
          <SelectorImage
            src={select.photo}
            alt="party-logo"
          />
          {/* eslint-disable-next-line max-len */}
          <SelectorLabel>{`${select?.name} (${select?.acronym})`}</SelectorLabel>
        </SelectorDisplay>
      ),
      label: (
        <SelectorLabel>{`${select?.name} (${select?.acronym})`}</SelectorLabel>
      ),
    })) || [];

  options.unshift({
    ID: 0,
    Name: "All Agencies",
    acronym: "all",
    label: <SelectorLabel>{"All Agencies"}</SelectorLabel>,
  });

  return options;
};

export const sortAgency = (column, sortDirection, tableOption) => {
  let newData = [];

  if (column.identifier === "budgetAllocation") {
    if (sortDirection === SORT_DIRECTION_ASC) {
      newData = tableOption.data.sort(
        (a, b) => a.budgetAllocation - b.budgetAllocation
      );
    } else {
      newData = tableOption.data.sort(
        (a, b) => b.budgetAllocation - a.budgetAllocation
      );
    }
  }

  if (column.identifier === "name") {
    newData = tableOption.data.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return sortDirection === SORT_DIRECTION_ASC ? -1 : 1;
      }

      if (nameA > nameB) {
        return sortDirection === SORT_DIRECTION_ASC ? 1 : -1;
      }

      return 0;
    });
  }

  return newData;
};

export const haveSameStatus = (tableOption) => {
  const referenceStatus = tableOption.data[0]?.status;

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < tableOption.data.length; i++) {
    if (tableOption.data[i]?.status !== referenceStatus) {
      return false;
    }
  }

  return true;
};

export const getProgramSelectorOptions = (data) => {
  const options =
    data?.map((select) => ({
      ID: select?.id,
      Name: select?.name,
      label: <h6 className="font-weight-bold mb-0">{select?.name}</h6>,
    })) || [];

  return options;
};

export const isSelectAllCheckboxShown = (role, checkedItems, data) => {
  if (canEditRoles.includes(role) && checkedItems.length === data.length) {
    return true;
  }

  return false;
};

const EDITOR_STATUS_OPTIONS = [
  ALLOCATION_STATUS.REQUESTED,
  ALLOCATION_STATUS.PENDING,
  ALLOCATION_STATUS.DISAPPROVED,
];

const APPROVED_AGENCY_STATUS_OPTIONS = [
  ALLOCATION_STATUS.PROCESSED,
  ALLOCATION_STATUS.DOWNLOADED,
  ALLOCATION_STATUS.RESUBMISSION,
  ALLOCATION_STATUS.NOTELIGIBLE,
];

const NOT_APPROVED_AGENCY_STATUS_OPTIONS = [
  ALLOCATION_STATUS.PROCESSED,
  ALLOCATION_STATUS.DOWNLOADED,
  ALLOCATION_STATUS.APPROVED,
];

const APPROVED_DIRECTOR_STATUS_OPTIONS = [
  ALLOCATION_STATUS.APPROVED,
  ALLOCATION_STATUS.PENDING,
  ALLOCATION_STATUS.DISAPPROVED,
  ALLOCATION_STATUS.RESUBMISSION,
  ALLOCATION_STATUS.NOTELIGIBLE,
];

const NOT_APPROVED_DIRECTOR_STATUS_OPTIONS = [
  ALLOCATION_STATUS.APPROVED,
  ALLOCATION_STATUS.PENDING,
  ALLOCATION_STATUS.DISAPPROVED,
];

export const getStatusOptions = (status, role) => {
  let options = ALLOCATION_STATUS_OPTIONS;

  if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
    if (status === ALLOCATION_STATUS.APPROVED) {
      options = options?.filter((option) =>
        APPROVED_DIRECTOR_STATUS_OPTIONS.includes(option.value)
      );
    } else {
      options = options?.filter((option) =>
        NOT_APPROVED_DIRECTOR_STATUS_OPTIONS.includes(option.value)
      );
    }
  }

  if (role === ACCOUNT_AGENCY) {
    if (status === ALLOCATION_STATUS.APPROVED) {
      options = options?.filter((option) =>
        APPROVED_AGENCY_STATUS_OPTIONS.includes(option.value)
      );
    } else {
      options = options?.filter((option) =>
        NOT_APPROVED_AGENCY_STATUS_OPTIONS.includes(option.value)
      );
    }
  }

  if (role === ACCOUNT_EDITOR) {
    options = options?.filter((option) =>
      EDITOR_STATUS_OPTIONS.includes(option.value)
    );
  }

  return options;
};

export const validate = ({
  formFields,
  agency,
  program,
  user,
  selectedCourses,
  uploadedFiles,
}) => {
  if (
    formFields.allocationType?.id === 2 &&
    !/^(09)\d{9}$/.test(formFields.allocationContactNumber)
  ) {
    return "Invalid ATC partner contact";
  }

  if (
    formFields.allocationType?.id === 3 &&
    !/^(09)\d{9}$/.test(formFields.allocationContactNumber)
  ) {
    return "Invalid contact number";
  }

  if (
    formFields.allocationType?.id === 3 &&
    !formFields.allocationContactName
  ) {
    return "Recipient Contact and Contact Number cannot be empty";
  }

  if (
    agency === AGENCY_TESDA_ID &&
    formFields.allocationType?.id !== 2 &&
    (program === 10 || program === 15) &&
    !formFields.tvi &&
    formFields.tviId !== null
  ) {
    return "Name of TVI cannot be empty";
  }

  if (
    agency === AGENCY_TESDA_ID &&
    formFields.allocationType?.id !== 2 &&
    (program === 10 || program === 15) &&
    selectedCourses.length === 0 &&
    formFields.tviId !== null
  ) {
    return "At least select one TVI Course";
  }

  const allHaveSlots = selectedCourses.every((course) => course.slots > 0);

  if (!allHaveSlots) {
    return "All selected courses must have slots greater than 0";
  }

  const allWithinAvailableSlots = selectedCourses.every((course) => {
    const maxSlots = course.oldSlotsValue + course.availableSlots;

    return course.slots <= maxSlots;
  });

  if (!allWithinAvailableSlots) {
    return "Total slots cannot exceed the allowed limit.";
  }

  if (
    agency === 8 &&
    formFields.allocationType?.id !== 2 &&
    !formFields.institution
  ) {
    return "Facility cannot be empty";
  }

  if (
    agency === 14 &&
    formFields.allocationType?.id !== 2 &&
    !formFields.institution
  ) {
    return "Project Title cannot be empty";
  }

  if (
    agency === 3 &&
    (formFields.allocationType?.id !== 2 ||
      formFields.allocationType?.id === 3) &&
    !formFields.institution &&
    Number(program) === 14
  ) {
    return "Project Title cannot be empty";
  }

  if (
    agency === 3 &&
    (formFields.allocationType?.id !== 2 ||
      formFields.allocationType?.id === 3) &&
    !formFields.otherInfo &&
    Number(program) === 14
  ) {
    return "LGSF Tracking Code cannot be empty";
  }

  if (
    agency === 14 &&
    formFields.allocationType?.id !== 2 &&
    !formFields.justification
  ) {
    return "Justification cannot be empty";
  }

  if (
    agency === 14 &&
    formFields.allocationType?.id !== 2 &&
    canEditRoles.includes(user?.type) &&
    !formFields.fundSource
  ) {
    return "Fund Source cannot be empty";
  }

  if (
    agency === 14 &&
    formFields.allocationType?.id !== 2 &&
    !formFields.implementingOffice
  ) {
    return "Implementing Office is required";
  }

  if (agency === 27 && uploadedFiles.length === 0) {
    return "Supporting documents cannot be empty";
  }

  return true;
};
